import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { Axios } from "../../../API/Axios";
import { useLanguage } from "../../../Context/LanguageContext";
import "./Orders.css";
import "./OrderDetail.css";

const STATUS_CLASS = {
    pending: "status-pending",
    processing: "status-processing",
    shipped: "status-shipped",
    delivered: "status-delivered",
    cancelled: "status-cancelled",
};

export default function OrderDetail ()
{
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, lang } = useLanguage();

    const [ order, setOrder ] = useState( null );
    const [ loading, setLoading ] = useState( true );
    const [ cancelling, setCancelling ] = useState( false );
    const [ error, setError ] = useState( "" );

    useEffect( () =>
    {
        Axios.get( `/orders/${ id }` )
            .then( ( res ) => setOrder( res.data ) )
            .catch( () => setOrder( null ) )
            .finally( () => setLoading( false ) );
    }, [ id ] );

    async function handleCancel ()
    {
        if ( !window.confirm( t( 'orders.confirmCancel' ) ) ) return;

        setCancelling( true );
        setError( "" );
        try
        {
            const res = await Axios.post( `/orders/${ id }/cancel` );
            setOrder( res.data );
        } catch ( err )
        {
            setError( err.response?.data?.message || t( 'orders.cancelError' ) );
        } finally
        {
            setCancelling( false );
        }
    }

    if ( loading )
    {
        return <div className="orders-page" />;
    }

    if ( !order )
    {
        return (
            <div className="orders-page">
                <div className="orders-empty-state">
                    <FontAwesomeIcon icon={ faBoxOpen } />
                    <p>{ t( 'orders.notFound' ) }</p>
                    <Link to="/orders" className="orders-login-cta">{ t( 'orders.backToProfile' ) }</Link>
                </div>
            </div>
        );
    }

    const statusText = t( `orders.status.${ order.status }` );
    const statusClass = STATUS_CLASS[ order.status ] || STATUS_CLASS.pending;

    return (
        <div className="orders-page">
            <div className="orders-container">
                <button className="od-back-btn" onClick={ () => navigate( -1 ) }>
                    <FontAwesomeIcon icon={ faArrowRight } />
                    { t( 'orders.backToProfile' ) }
                </button>

                <div className="orders-card">
                    <div className="orders-card-header">
                        <span className="orders-card-id">
                            { t( 'orders.orderNumber', { id: order.id } ) }
                        </span>
                        <div className="od-status-badges">
                            <span className={ `orders-card-status ${ statusClass }` }>
                                { statusText }
                            </span>
                            <span className={ `od-payment-badge od-payment-badge--${ order.payment_status }` }>
                                { t( `orders.paymentStatus.${ order.payment_status }` ) }
                            </span>
                        </div>
                    </div>
                    <p className="orders-card-date">
                        { new Date( order.created_at ).toLocaleDateString( lang === 'ar' ? 'ar-EG' : 'en-US' ) }
                    </p>

                    { order.address && (
                        <div className="od-shipping-box">
                            <p><strong>{ t( 'checkout.shippingTitle' ) }:</strong> { order.address }, { order.city }</p>
                            <p>{ order.phone }</p>
                            { order.notes && <p className="od-notes">{ order.notes }</p> }
                        </div>
                    ) }

                    { order.tracking_number && (
                        <div className="od-tracking-box">
                            <p className="od-tracking-label">{ t( 'orders.trackingTitle' ) }</p>
                            { order.carrier_name && (
                                <p className="od-tracking-row">
                                    <span>{ t( 'orders.carrier' ) }</span>
                                    <strong>{ order.carrier_name }</strong>
                                </p>
                            ) }
                            <p className="od-tracking-row">
                                <span>{ t( 'orders.trackingNumber' ) }</span>
                                <strong>{ order.tracking_number }</strong>
                            </p>
                            { order.tracking_url && (
                                <a
                                    href={ order.tracking_url }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="od-track-btn"
                                >
                                    { t( 'orders.trackShipment' ) }
                                </a>
                            ) }
                        </div>
                    ) }

                    <div className="orders-card-items">
                        { order.items?.map( ( orderItem ) =>
                        {
                            const product = orderItem.product || {};
                            const img = product.images?.[ 0 ]?.image;
                            return (
                                <div className="orders-item-row" key={ orderItem.id }>
                                    { img ? (
                                        <img src={ img } alt={ product.title || "" } className="orders-item-img" />
                                    ) : (
                                        <div className="orders-item-img orders-item-img--placeholder" />
                                    ) }
                                    <div className="orders-item-info">
                                        <p className="orders-item-title">{ product.title }</p>
                                        <p className="orders-item-sub">
                                            { orderItem.count } × { Number( orderItem.price ).toFixed( 2 ) } { t( 'currency' ) }
                                        </p>
                                    </div>
                                </div>
                            );
                        } ) }
                    </div>

                    <div className="orders-card-total">
                        <span>{ t( 'orders.total' ) }</span>
                        <strong>{ Number( order.total ).toFixed( 2 ) } { t( 'currency' ) }</strong>
                    </div>

                    { error && <p className="od-error">{ error }</p> }

                    { order.status === 'pending' && (
                        <button
                            className="od-cancel-btn"
                            onClick={ handleCancel }
                            disabled={ cancelling }
                        >
                            { cancelling ? t( 'orders.cancelling' ) : t( 'orders.cancelOrder' ) }
                        </button>
                    ) }
                </div>
            </div>
        </div>
    );
}
